import React, { useEffect, useRef, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { AdminLayout } from "@/pages/admin/AdminLayout";
import { toast } from "sonner";
import { Save, Upload, Image as ImageIcon } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const abs = (u) => (u && u.startsWith("/") ? `${BACKEND}${u}` : u);
const SLOTS = [["logo_primary", "Primary logo (dark on light)"], ["logo_light", "Light/white logo (on dark)"], ["logo_dark", "Dark logo"], ["seal", "Institutional seal"]];

export default function AdminBranding() {
  const [b, setB] = useState(null);
  const [busy, setBusy] = useState("");
  const refs = useRef({});
  const load = () => api.get("/admin/branding").then((r) => setB(r.data));
  useEffect(() => { load(); }, []);
  if (!b) return <AdminLayout><div className="p-8 text-paper/40">Loading…</div></AdminLayout>;
  const set = (k, v) => setB({ ...b, [k]: v });

  const save = async () => {
    try { await api.put("/admin/branding", b); toast.success("Branding saved — used by all documents"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const upload = async (slot, file) => {
    if (!file) return;
    setBusy(slot);
    const fd = new FormData(); fd.append("file", file);
    try { const r = await api.post(`/admin/branding/logo?slot=${slot}`, fd, { headers: { "Content-Type": "multipart/form-data" } }); toast.success("Logo uploaded"); setB((cur) => ({ ...cur, [slot]: r.data.url })); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    setBusy("");
  };
  const I = "mt-1 w-full bg-[#0B0D12] border border-white/10 rounded-sm px-3 py-2 text-sm text-paper";

  return (
    <AdminLayout>
      <div className="p-8 max-w-4xl" data-testid="admin-branding-page">
        <h1 className="font-serif text-3xl">Institutional Branding</h1>
        <p className="text-paper/50 text-sm mt-1">These assets are the default identity for every SIMO-generated document (Doc Studio, reports, letters). Upload your official logos to override.</p>

        <div className="mt-6 grid sm:grid-cols-2 gap-4" data-testid="branding-logos">
          {SLOTS.map(([slot, label]) => (
            <div key={slot} className="border border-white/10 rounded-sm p-4 bg-white/5">
              <div className="text-xs uppercase tracking-wide text-paper/40 mb-2">{label}</div>
              <div className="h-20 flex items-center justify-center bg-white rounded-sm overflow-hidden mb-3">
                {b[slot] ? <img src={abs(b[slot])} alt={label} className="max-h-16 max-w-full object-contain" data-testid={`branding-preview-${slot}`} /> : <ImageIcon className="h-6 w-6 text-slate-300" />}
              </div>
              <label className="inline-flex items-center gap-1.5 border border-white/15 px-3 py-1.5 rounded-sm text-sm cursor-pointer hover:bg-white/5" data-testid={`branding-upload-${slot}`}>
                <Upload className="h-4 w-4" /> {busy === slot ? "Uploading…" : "Upload"}
                <input ref={(el) => (refs.current[slot] = el)} type="file" accept="image/*" className="hidden" onChange={(e) => upload(slot, e.target.files?.[0])} />
              </label>
            </div>
          ))}
        </div>

        <div className="mt-6 grid sm:grid-cols-2 gap-4">
          <label className="text-xs text-paper/50">Organisation name<input value={b.org_name || ""} onChange={(e) => set("org_name", e.target.value)} className={I} data-testid="branding-org" /></label>
          <label className="text-xs text-paper/50">Accent colour<input value={b.accent || ""} onChange={(e) => set("accent", e.target.value)} className={I} data-testid="branding-accent" placeholder="#5B3DF5" /></label>
          <label className="text-xs text-paper/50">Footer left (contact)<input value={b.footer_left || ""} onChange={(e) => set("footer_left", e.target.value)} className={I} data-testid="branding-footer-left" /></label>
          <label className="text-xs text-paper/50">Footer right (site)<input value={b.footer_right || ""} onChange={(e) => set("footer_right", e.target.value)} className={I} data-testid="branding-footer-right" /></label>
        </div>

        <button onClick={save} data-testid="branding-save" className="mt-6 inline-flex items-center gap-1.5 bg-heraldic text-white px-5 py-2.5 rounded-sm text-sm"><Save className="h-4 w-4" /> Save branding</button>
      </div>
    </AdminLayout>
  );
}
